import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Truck, Package, Clock, MapPin, AlertCircle } from 'lucide-react';

const Shipping = () => {
  return (
    <div className="pb-16 bg-brand-bone min-h-screen" data-testid="shipping-page">
      {/* Hero */}
      <section className="bg-brand-black/[0.03] py-12 md:py-20 border-b border-brand-cobalt/10">
        <div className="max-w-4xl mx-auto px-4">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-brand-cobalt hover:text-brand-cobalt/80 mb-6"
          >
            <ArrowLeft size={16} />
            Volver al inicio
          </Link>
          <h1 className="font-display text-3xl md:text-5xl tracking-tight mb-4 text-brand-black">
            Política de Envíos
          </h1>
          <p className="text-brand-black/60 text-lg">
            Información sobre envíos y entregas
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4">
          {/* Info Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="brand-card hybrid-glow p-6 rounded-lg">
              <Truck className="text-brand-cobalt mb-4" size={32} />
              <h3 className="font-display text-lg mb-2 text-brand-black">Cobertura Nacional</h3>
              <p className="text-brand-black/60 text-sm">Envíos a todo el territorio de México</p>
            </div>
            <div className="brand-card hybrid-glow p-6 rounded-lg">
              <Clock className="text-brand-cobalt mb-4" size={32} />
              <h3 className="font-display text-lg mb-2 text-brand-black">Procesamiento</h3>
              <p className="text-brand-black/60 text-sm">1-2 días hábiles después de confirmar pago</p>
            </div>
            <div className="brand-card hybrid-glow p-6 rounded-lg">
              <Package className="text-brand-cobalt mb-4" size={32} />
              <h3 className="font-display text-lg mb-2 text-brand-black">Zonas Urbanas</h3>
              <p className="text-brand-black/60 text-sm">2-5 días hábiles de entrega</p>
            </div>
            <div className="brand-card hybrid-glow p-6 rounded-lg">
              <MapPin className="text-brand-cobalt mb-4" size={32} />
              <h3 className="font-display text-lg mb-2 text-brand-black">Zonas Extendidas</h3>
              <p className="text-brand-black/60 text-sm">3-7 días hábiles de entrega</p>
            </div>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2 className="font-display text-xl md:text-2xl mt-8 mb-4 text-brand-cobalt">Procesamiento de pedidos</h2>
            <p className="text-brand-black/80 mb-4">
              Todos los pedidos realizados a través del sitio web de Alphatica son procesados dentro de
              un plazo aproximado de <strong className="text-brand-black">1 a 2 días hábiles</strong>, una vez confirmado el pago.
            </p>
            <p className="text-brand-black/80 mb-6">
              Los pedidos realizados en fines de semana o días festivos comenzarán a procesarse el
              siguiente día hábil.
            </p>

            <h2 className="font-display text-xl md:text-2xl mt-8 mb-4 text-brand-cobalt">Tiempo de entrega</h2>
            <p className="text-brand-black/80 mb-4">
              El tiempo estimado de entrega puede variar dependiendo de la ubicación del cliente y de la
              empresa de mensajería utilizada. En promedio, los tiempos de entrega son:
            </p>
            <ul className="space-y-2 text-brand-black/80 mb-6">
              <li className="flex items-start gap-2">
                <span className="text-brand-petroleum">•</span>
                <strong className="text-brand-black">2 a 5 días hábiles</strong> en zonas urbanas
              </li>
              <li className="flex items-start gap-2">
                <span className="text-brand-petroleum">•</span>
                <strong className="text-brand-black">3 a 7 días hábiles</strong> en zonas extendidas
              </li>
            </ul>

            <h2 className="font-display text-xl md:text-2xl mt-8 mb-4 text-brand-cobalt">Empaque</h2>
            <p className="text-brand-black/80 mb-6">
              Todos los productos son cuidadosamente empacados con materiales de protección para
              minimizar cualquier riesgo de daño durante el transporte.
            </p>

            <h2 className="font-display text-xl md:text-2xl mt-8 mb-4 text-brand-cobalt">Seguimiento de pedidos</h2>
            <p className="text-brand-black/80 mb-6">
              Cuando el pedido sea enviado, el cliente podrá recibir un número de guía o confirmación de
              envío para realizar el seguimiento del paquete.
            </p>

            <div className="brand-card hybrid-glow border-l-4 border-amber-500 p-4 my-8">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <h3 className="font-medium text-amber-500 mb-2">Responsabilidad del envío</h3>
                  <p className="text-brand-black/70 text-sm mb-2">
                    Una vez que el pedido es entregado a la empresa de mensajería, Alphatica no se hace
                    responsable por retrasos ocasionados por factores externos como:
                  </p>
                  <ul className="text-brand-black/70 text-sm space-y-1">
                    <li>• Retrasos logísticos</li>
                    <li>• Condiciones climáticas</li>
                    <li>• Saturación en servicios de paquetería</li>
                    <li>• Causas de fuerza mayor</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Contact CTA */}
          <div className="mt-12 brand-card hybrid-glow p-6 rounded-lg text-center border border-brand-cobalt/30">
            <h3 className="font-display text-xl mb-2 text-brand-black">¿Tienes dudas sobre tu envío?</h3>
            <p className="text-brand-black/60 mb-4">Contáctanos y te ayudamos</p>
            <Link 
              to="/contact" 
              className="inline-flex items-center gap-2 brand-btn rounded"
            >
              Contactar
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Shipping;
